import React from "react";
import { FieldArray } from "formik";
import { Button } from "@mui/material";
import FormField from "./FormField";

type FieldArrayValue = string | { startDate: string; endDate: string };

type GenericFieldArrayProps = {
  name: string;
  label: string;
  fieldType: string;
  maxFields?: number;
  addFiledText?: string;
  removeFiledText?: string;
  [key: string]: unknown;
};

type GenericFormikState = {
  values: Record<string, unknown>;
  touched: Record<string, unknown>;
  errors: Record<string, unknown>;
  setFieldValue: (
    _field: string,
    _value: unknown,
    _shouldValidate?: boolean
  ) => void;
  setFieldTouched: (
    _field: string,
    _isTouched?: boolean,
    _shouldValidate?: boolean
  ) => void;
  setFieldError: (_field: string, _message?: string) => void;
  validateField: (_field: string) => void | Promise<unknown>;
};

type GenericArrayHelpers = {
  form: GenericFormikState;
  push: (_value: FieldArrayValue) => void;
  remove: (_index: number) => void;
};

const getDefaultValue = (fieldType: string): FieldArrayValue =>
  fieldType === "dateRange" ? { startDate: "", endDate: "" } : "";

const getArrayValue = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [];

const GenericFieldArray = ({
  name,
  label,
  fieldType,
  maxFields = 5,
  addFiledText = "+ Add",
  removeFiledText = "Remove",
  ...props
}: GenericFieldArrayProps) => {
  return (
    <FieldArray name={name}>
      {(arrayHelpers) => {
        const typedArrayHelpers =
          arrayHelpers as unknown as GenericArrayHelpers;
        const { form } = typedArrayHelpers;

        const currentValue = form.values[name];
        let values = getArrayValue(currentValue);

        if (values.length === 0) {
          const defaultValue = getDefaultValue(fieldType);
          values = [defaultValue];
          form.setFieldValue(name, values);
        }

        const handleAddNew = () => {
          const currentValues = getArrayValue(form.values[name]);
          if (currentValues.length < maxFields) {
            const defaultValue = getDefaultValue(fieldType);
            typedArrayHelpers.push(defaultValue);
          }
        };

        const handleRemoveLast = () => {
          const currentValues = getArrayValue(form.values[name]);
          if (currentValues.length > 1) {
            typedArrayHelpers.remove(currentValues.length - 1);
          }
        };

        const renderValues = getArrayValue(form.values[name]);

        return (
          <div className="w-100">
            {renderValues.map((_fieldValue, index) => {
              const hasError =
                form.touched[name] || form.touched[`${name}.${index}`];
              const fieldErrors = form.errors?.[name];
              const indexedErrors = Array.isArray(fieldErrors)
                ? fieldErrors[index]
                : undefined;

              return (
                <div
                  key={`${name}-${index}`}
                  className="d-flex align-items-start"
                >
                  {/* Field with error */}
                  <div className="flex-grow-1">
                    <FormField
                      type={fieldType}
                      name={`${name}.${index}`}
                      label={`${label}`}
                      onChange={() => {
                        form.setFieldTouched(name, true, false);
                        Promise.resolve(form.validateField(name)).then(() => {
                          if (!form.errors[name]) {
                            form.setFieldError(name, undefined);
                          }
                        });
                      }}
                      {...props}
                    />

                    {hasError && (
                      <div
                        className="text-danger mt-1 mb-2 ml-2"
                        style={{ fontSize: "12px" }}
                      >
                        {indexedErrors &&
                          Object.values(
                            indexedErrors as Record<string, React.ReactNode>
                          ).map((errMsg, i) => <div key={i}>{errMsg}</div>)}
                      </div>
                    )}
                  </div>

                  {/* Add / Remove buttons inline with field */}
                  {index === 0 && (
                    <div className="d-flex align-items-center ms-2 mt-4">
                      {renderValues.length < maxFields && (
                        <Button
                          type="button"
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={handleAddNew}
                          className="me-2 p-2"
                        >
                          {addFiledText}
                        </Button>
                      )}
                      {renderValues.length > 1 && (
                        <Button
                          type="button"
                          variant="outlined"
                          size="small"
                          onClick={handleRemoveLast}
                          className="p-2 ml-2"
                        >
                          {removeFiledText}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      }}
    </FieldArray>
  );
};

export default GenericFieldArray;
